/**
 * MilestoneFunnel.tsx — Section 1
 * Milestone metric cards + a descending spark-funnel labeled with count and
 * % of new Members.
 *
 * Everything derives from the endpoint's milestones[] — which cards render,
 * how many, and in what order is the API's decision (m.card), so hiding or
 * re-introducing a milestone (e.g. a second free chapter) needs no client
 * change. The old hardcoded CARD_KEYS list silently rendered a 3-card grid
 * whenever a key went missing; that failure mode is gone by construction.
 *
 * Numbers policy: safeMetric for every displayed value — a null renders '—',
 * never a fabricated 0 (this file previously ?? 0'd its percentages).
 */

import React from 'react';
import { Box, Typography, Paper, Skeleton } from '@mui/material';
import SectionHeader from './SectionHeader';
import { FONT, FONT_H, FONT_MONO, MILESTONE_COLORS, SURFACE, convPctColor } from './funnelBands';
import { Milestone } from './activationTypes';
import { safeMetric } from '../../utils/safeMetric';

interface Props {
  milestones: Milestone[];
  funnelTop: number;
  downloadClicks: number;
  loading: boolean;
}

// Editorial captions per milestone key. Purely optional flavor — an unknown
// key simply renders no caption.
const TIPS: Record<string, string> = {
  install: 'Acquisition baseline — every downstream rate is a share of this.',
  first_chapter: 'The value moment. Only those who reach it ever feel the product work.',
  second_chapter: 'A strong hop — those who make one chapter mostly make a second.',
  subscriber: 'Once the habit forms, conversion to paid is healthy.',
};

const MilestoneFunnel: React.FC<Props> = ({ milestones, funnelTop, downloadClicks, loading }) => {
  // API-driven: cards are the milestones the server marks card:true (legacy
  // responses without the flag fall back to "everything but 'account'").
  const cards = milestones.filter(m => (m.card !== undefined ? m.card : m.key !== 'account'));

  return (
    <Box>
      <SectionHeader
        num={1}
        title="Milestone Funnel"
        description={`Shows how many of the ${funnelTop.toLocaleString()} new Members in this period reach each major milestone, as a clean descending funnel. Steps count Members of this cohort wherever they are today.`}
      />

      {/* ── Metric cards ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: `repeat(${Math.max(cards.length, 2)}, 1fr)` }, gap: 1.5, mb: 2 }}>
        {(loading ? Array(3).fill(null) : cards).map((m: Milestone | null, idx: number) => {
          const color = m ? (MILESTONE_COLORS[m.key] || '#1A5F7A') : '#1A5F7A';
          const isBaseline = m?.key === 'install';
          const chipColor = m && !isBaseline ? convPctColor(m.prev_step_pct) : null;
          // Unclamped by design — above 100% means this milestone counts
          // Members its predecessor misses; render the data-check, never a
          // green impossible percentage (D3.2).
          const prevPctIsCheck = m != null && m.prev_step_pct != null && m.prev_step_pct > 100;
          return (
            <Box key={m?.key ?? idx}>
              {loading || !m ? (
                <Skeleton variant="rounded" height={150} sx={{ borderRadius: '13px' }} />
              ) : (
                <Paper elevation={0} sx={{
                  bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`,
                  borderTop: `4px solid ${color}`, borderRadius: '13px', p: '14px 16px', height: '100%',
                }}>
                  <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', letterSpacing: '0.04em', textTransform: 'uppercase', color: SURFACE.slate }}>
                    {m.label}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT_MONO, fontSize: '32px', fontWeight: 800, lineHeight: 1, mt: 1, mb: 0.25, color }}>
                    {safeMetric(m.value?.toLocaleString())}
                  </Typography>
                  <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: SURFACE.slate }}>
                    {isBaseline
                      ? (m.value > 0 ? '100% · baseline' : '— · baseline (no new Members this period)')
                      : safeMetric(m.pct_of_installs != null ? `${m.pct_of_installs.toFixed(1)}% of new Members` : null)}
                  </Typography>
                  {/* Download-page clicks: a plain count. The old "click-to-install %"
                      divided two unrelated cohorts (web clicks vs. registrations)
                      and printed 3975% — a ratio with no shared denominator. */}
                  {isBaseline && downloadClicks > 0 && (
                    <Box sx={{ display: 'inline-block', mt: 0.9, bgcolor: '#e6f0ec', borderRadius: '5px', px: 0.9, py: 0.25 }}>
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', fontWeight: 600, color: '#146152' }}>
                        {downloadClicks.toLocaleString()} download-page clicks
                      </Typography>
                    </Box>
                  )}
                  {!isBaseline && prevPctIsCheck && (
                    <Box sx={{ display: 'inline-block', mt: 0.9, bgcolor: '#fdf0e3', borderRadius: '5px', px: 0.9, py: 0.25 }}>
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', fontWeight: 600, color: '#B45309' }}>
                        ⚠ data check — above prev step
                      </Typography>
                    </Box>
                  )}
                  {!isBaseline && !prevPctIsCheck && m.prev_step_pct != null && chipColor && (
                    <Box sx={{ display: 'inline-block', mt: 0.9, bgcolor: `${chipColor}1a`, borderRadius: '5px', px: 0.9, py: 0.25 }}>
                      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '9.5px', fontWeight: 600, color: chipColor }}>
                        {m.prev_step_pct.toFixed(0)}% from prev step
                      </Typography>
                    </Box>
                  )}
                  {TIPS[m.key] && (
                    <Typography sx={{ fontFamily: FONT, fontSize: '10.5px', color: SURFACE.slate, mt: 0.9, fontStyle: 'italic', lineHeight: 1.4 }}>
                      {TIPS[m.key]}
                    </Typography>
                  )}
                </Paper>
              )}
            </Box>
          );
        })}
      </Box>

      {/* ── Spark funnel ── */}
      {loading ? (
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '13px' }} />
      ) : (
        <SparkFunnel milestones={milestones} funnelTop={funnelTop} />
      )}

      <Typography sx={{ fontFamily: FONT, fontSize: '12px', color: SURFACE.slate, fontStyle: 'italic', mt: 1.5 }}>
        Each milestone is a reconciled count of the same Member cohort, so the funnel descends cleanly. The biggest gap is your highest-leverage work.
      </Typography>
    </Box>
  );
};

const SparkFunnel: React.FC<{ milestones: Milestone[]; funnelTop: number }> = ({ milestones, funnelTop }) => {
  // Bars scale against the baseline itself, matching the caption ("% of new
  // Members"). The old code scaled against the tallest milestone, so the bars
  // and their printed percentages used different bases.
  const base = Math.max(funnelTop, 1);
  return (
    <Paper elevation={0} sx={{ bgcolor: SURFACE.paper, border: `1px solid ${SURFACE.line}`, borderRadius: '13px', p: '20px 18px 34px' }}>
      <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10px', letterSpacing: '0.05em', textTransform: 'uppercase', color: SURFACE.slate, mb: 2.5 }}>
        Funnel shape · count + % of new Members on each bar
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 160 }}>
        {milestones.map(m => {
          const color = MILESTONE_COLORS[m.key] || '#1A5F7A';
          const hPct = Math.min(100, Math.max(8, ((m.value ?? 0) / base) * 100));
          const lightText = m.key !== 'subscriber'; // gold bar needs dark text
          return (
            <Box key={m.key} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', height: '100%', position: 'relative' }}>
              <Box sx={{
                width: '100%', height: `${hPct}%`, minHeight: 8, bgcolor: color,
                borderRadius: '6px 6px 0 0', display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'flex-start', pt: 0.75,
                '@media (prefers-reduced-motion: no-preference)': { transition: 'height 0.4s ease' },
              }}>
                <Typography sx={{ fontFamily: FONT_MONO, fontSize: '13px', fontWeight: 700, lineHeight: 1, color: lightText ? '#fff' : SURFACE.ink }}>
                  {safeMetric(m.value?.toLocaleString())}
                </Typography>
                <Typography sx={{ fontFamily: FONT_MONO, fontSize: '10px', fontWeight: 600, lineHeight: 1, mt: 0.4, color: lightText ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.6)' }}>
                  {safeMetric(m.pct_of_installs != null ? `${m.pct_of_installs.toFixed(1)}%` : null)}
                </Typography>
              </Box>
              <Typography sx={{ position: 'absolute', bottom: -26, fontFamily: FONT_MONO, fontSize: '9px', color: SURFACE.slate, letterSpacing: '0.04em', textTransform: 'uppercase', textAlign: 'center', lineHeight: 1.15 }}>
                {m.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Paper>
  );
};

export default MilestoneFunnel;
